<template>
	<div class="xs-12">
		<!-- Headline -->
		<div slot="headline" class="fs5 lh125 fw7 c1 mb2">{{ $t("actions") }}</div>

		<!-- Body -->
		<j-card class="bg5 bg4--l3">
			<!-- Request Payment -->
			<div
				v-if="$store.state.settings.quotes.actions.hasRequestPayment"
				class="flex xs-12 xs-middle xs-between p2 bb1 bc7"
			>
				<div class="fs3 fw7 c6 lh125">{{ $t("request_payment") }}</div>
				<j-button
					size="xsmall"
					:isLoading="isRequestPaymentLoading"
					v-tooltip="requestPaymentTooltip"
					@click="requestPayment"
					action="requestPayment"
					>{{ $t("request") }}</j-button
				>
			</div>

			<!-- send quote  -->
			<div
				v-if="hasSendQuote"
				class="flex xs-12 xs-middle xs-between p2 bb1 bc7"
			>
				<div class="fs3 fw7 c6 lh125">{{ $t("send_quote") }}</div>
				<j-button
					size="xsmall"
					v-tooltip="quoteTooltip"
					:isLoading="isSendQuoteLoading"
					@click="$modal.show('send_quote')"
					action="requestQuote"
					>{{ $t("send_quote") }}</j-button
				>
			</div>

			<!-- Issue -->
			<div
				v-if="$store.state.settings.quotes.actions.hasIssuePolicy"
				class="flex xs-12 xs-middle xs-between p2 bb1 bc7"
			>
				<div class="xs-8">
					<div class="xs-12 fs3 fw7 c6 lh125">{{ $t("issue_policy") }}</div>
				</div>
				<j-button
					:isLoading="isIssueLoading"
					v-tooltip="issueTooltip"
					size="xsmall"
					@click="issuePolicy"
					action="issuePolicy"
					>{{ $t("issue") }}</j-button
				>
			</div>

			<!-- Issue Manually -->
			<div
				v-if="$store.state.settings.quotes.actions.hasIssueExistingPolicy"
				class="flex xs-12 xs-middle xs-between p2"
			>
				<div class="xs-8">
					<div class="xs-12 fs3 fw7 c6 lh125">
						{{ $t("issue_policy_manually") }}
					</div>
				</div>
				<j-button
					v-tooltip="issueManuallyTooltip"
					size="xsmall"
					@click="validateAcceptedOffer() ? $modal.show('issueManually') : null"
				>
					{{ $t("issue_manually") }}
				</j-button>
			</div>
		</j-card>

		<!-- Modals -->
		<core-modal name="send_quote" :headline="$t('send_quote')">
			<div class="p4">
				<div class="lh150">
					<div class="lh150"></div>
				</div>

				<core-form-input
					componentName="j-base-input-text"
					name="email"
					:label="$t('recipient_email')"
					v-validate="{ required: true, email: true }"
					v-model="form.to"
					id="email"
					:validationErrors="errors"
				/>

				<div class="flex xs-between mt6">
					<j-button
						:isLoading="isSendQuoteLoading"
						@click="sendQuoteToInsurer"
						action="send"
						>{{ $t("send") }}</j-button
					>
					<j-button @click="$modal.hide('send_quote')" type="bordered">{{
						$t("cancel")
					}}</j-button>
				</div>
			</div>
		</core-modal>

		<core-modal name="requestPaymentWarning" :headline="$t('request_payment')">
			<div class="p4">
				<h4 class="mb2">{{ $t("warning_heading") }}</h4>
				<ul class="ml4 lh150">
					<li
						class="xs-12 fs3 fw7 c6 lh125 mb2"
						v-for="(warning, i) in this.warnings"
						:key="i"
					>
						<span>{{ warning }}</span>
					</li>
				</ul>
			</div>

			<div class="xs-12 xs-around p4 flex">
				<j-button
					class="tertiary"
					@click="$modal.hide('requestPaymentWarning')"
					>{{ $t("dismiss") }}</j-button
				>
				<j-button
					v-tooltip="
						'You may send a request for payment regardless of the above warnings'
					"
					@click="this.requestAnyway"
					:isLoading="isRequestPaymentLoading"
					action="continueRequestPayment"
					>{{ $t("continue") }}</j-button
				>
			</div>
		</core-modal>

		<core-modal name="issuePolicyWarning" :headline="$t('issue_policy')">
			<div class="p4">
				<h4 class="mb2">{{ $t("warning_heading") }}</h4>
				<ul class="ml4 lh150">
					<li
						class="xs-12 fs3 fw7 c6 lh125 mb2"
						v-for="(warning, i) in this.warnings"
						:key="i"
					>
						<span>{{ warning }}</span>
					</li>
				</ul>
			</div>

			<div class="xs-12 xs-around p4 flex">
				<j-button
					class="tertiary"
					@click="$modal.hide('requestPaymentWarning')"
					>{{ $t("dismiss") }}</j-button
				>
				<j-button
					v-tooltip="'You may issue policy regardless of the above warnings'"
					@click="this.issueAnyway"
					:isLoading="isIssueLoading"
					>{{ $t("continue") }}</j-button
				>
			</div>
		</core-modal>

		<core-modal name="issueManually" :headline="$t('issue_policy_manually')">
			<div class="p4">
				<div class="lh125 mb2">
					{{ $t("issue_manually_description") }}
				</div>
				<core-form-input
					class="mt4"
					componentName="j-base-input-text"
					name="issuerNumber"
					label="Policy Number"
					v-model="issuerNumber"
				/>
			</div>

			<div class="xs-12 xs-between p4 flex">
				<j-button
					class="bordered"
					:isDisabled="isIssueManuallyLoading"
					@click="$modal.hide('issueManually')"
				>
					{{ $t("cancel") }}</j-button
				>
				<j-button
					@click="this.issuePolicyManually"
					:isLoading="isIssueManuallyLoading"
				>
					{{ $t("issue_policy") }}
				</j-button>
			</div>
		</core-modal>
	</div>
</template>

<script>
import CoreModal from "@/modules/core/components/CoreModal"
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import JCard from "@/components/JCard"
import JButton from "@/components/JButton"
import get from "lodash.get"
import api from "../api/"

import {
	generatePaymentNotification,
	generateRequestQuoteFromInsurer
} from "@/clientSettings/templates/"

/**
 * This component is responsible for numerous actions that can happen on policy
 * It's a smart component that is calling APIs
 */
export default {
	components: { JCard, JButton, CoreModal, CoreFormInput },

	props: {
		acceptedOffer: {
			type: Object
		},
		quoteId: {
			type: String,
			required: true,
			default: ""
		},
		amount: {
			type: Number,
			required: true,
			default: 0
		},
		currencyCode: {
			type: String,
			required: true,
			default: ""
		},
		/**
		 * TODO: hacking here
		 * We are passing the whole data object we get for quote
		 * It's a policy type thta we get from GraphQL
		 * We do it because we need to construct email template to be sent and for that we need some data to populate the template
		 * It shouldn't work like this, but we have improvised email templating now
		 * After we have better system for email templating handling, this should be removed
		 */
		context: {
			type: Object,
			required: true,
			default: () => {}
		}
	},

	data() {
		return {
			isRequestPaymentLoading: false,
			isIssueLoading: false,
			isIssueManuallyLoading: false,
			isSendQuoteLoading: false,
			form: {
				to: null
			},
			forceIssue: false,
			warnings: null,

			// For manual issuance
			issuerNumber: null
		}
	},

	computed: {
		requestPaymentTooltip() {
			return {
				placement: "auto",
				content: this.$t("request_payment_description"),
				delay: { show: 100 }
			}
		},

		issueTooltip() {
			return {
				placement: "auto",
				content: this.$t("issue_description"),
				delay: { show: 100 }
			}
		},

		issueManuallyTooltip() {
			return {
				placement: "auto",
				content: this.$t("issue_manually_description"),
				delay: { show: 100 }
			}
		},

		quoteTooltip() {
			return {
				placement: "auto",
				content: this.$t("send_quote_to_insurer_description"),
				delay: { show: 500 }
			}
		},

		hasSendQuote() {
			return this.$store.state.settings.quotes.actions?.hasSendQuote
		}
	},

	methods: {
		async requestAnyway() {
			this.forceIssue = true
			await this.requestPayment()

			this.$modal.hide("requestPaymentWarning")

			this.forceIssue = false
			this.warnings = null
		},
		async issueAnyway() {
			this.forceIssue = true
			await this.issuePolicy()

			this.$modal.hide("issuePolicyWarning")

			this.forceIssue = false
			this.warnings = null
		},
		async requestPayment() {
			if (!this.acceptedOffer) {
				this.$store.dispatch("showError", this.$t("no_accepted_offer"))
				this.isRequestPaymentLoading = false
				return
			}

			// Start of loading
			this.isRequestPaymentLoading = true

			// 0: Validate policy
			// If invalid, just return. Errors are being shows in validatePolicy method
			const isPolicyValid = await this.validatePolicy()
			if (!isPolicyValid && !this.forceIssue) {
				this.isRequestPaymentLoading = false
				this.$modal.show("requestPaymentWarning")
				return
			}

			// prettier-ignore
			const redirectUrls = {
				jetco: `https://api.asia.covergo.com/j/${this.$store.state.tenantId}/covergo_crm/(transactionId)/p/0/en`,
				stripe: ""
			}

			const getRedirectUrlForPaymentProvider = (provider = "jetco") => {
				return redirectUrls[provider] || null
			}

			// 1: Initialize payment call
			const redirectToUrl = getRedirectUrlForPaymentProvider(
				this.$store.state.settings.paymentProvider
			)

			const res = await api.initializeTransaction({
				input: {
					policyId: this.quoteId,
					amount: this.amount,
					currencyCode: this.currencyCode,
					redirectToUrl: `https://api.asia.covergo.com/j/${this.$store.state.tenantId}/covergo_crm/(transactionId)/p/0/en`
				}
			})

			// 2: Send email
			// When successfull, send email notification with a link to our Jetco bridge app
			const url = get(res, "data.initializeTransaction.value.url")
			const token = get(res, "data.initializeTransaction.value.token")
			const transactionId = get(
				res,
				"data.initializeTransaction.value.transactionId"
			)

			/**
			 * Helper function to get email from contacts of holder
			 */
			const getHolderEmail = contacts => {
				const found = contacts.find(item => item.type === "email")
				return found.value
			}

			// prettier-ignore
			const paymentLinks = {
				jetco: `https://payments.asia.covergo.com/bridge?url=${encodeURIComponent(url)}&token=${encodeURIComponent(token)}&provider=jetco`,
				stripe: `https://payments.asia.covergo.com/gateway?provider=stripe&transactionId=${transactionId}&amount=${this.amount}&tenantId=${this.$store.state.tenantId}&code=${encodeURIComponent(this.currencyCode)}`
			}

			const emailContent = generatePaymentNotification(
				this.$store.state.tenantId,
				{
					toEmail: getHolderEmail(this.context.holder.contacts),
					policyNumber: "---",
					policyHolders: [
						this.context.holder.name,
						this.context.otherHolders
							? this.context.otherHolders.map(item => item.name)
							: ""
					],
					insuredName: get(this, "context.insured[0].name", ""),
					paymentLink: paymentLinks[this.$store.state.settings.paymentProvider],
					context: this.context
				}
			)

			// return early if there are no templates for tenant
			if (!emailContent) {
				// prettier-ignore
				this.$store.dispatch("showError", this.$t("email_has_no_template_for_tenant"))
				this.isRequestPaymentLoading = false
				return
			}

			// Send
			// prettier-ignore
			const sendEmailRes = await api.sendNotification({
				input: {
					emailMessage: emailContent
				}
			})

			// Check errors
			if (sendEmailRes.errors) {
				this.$store.dispatch("showError", sendEmailRes.errors)
				this.isRequestPaymentLoading = false
				return
			}

			// Show success message
			this.$store.dispatch("showSuccess", this.$t("email_was_sent_to_client"))

			// End of loading
			this.isRequestPaymentLoading = false
		},
		async issuePolicy() {
			if (this.isIssueLoading === true) return // if it's already in progress

			if (!this.acceptedOffer) {
				this.$store.dispatch("showError", this.$t("no_accepted_offer"))
				this.isIssueLoading = false
				return
			}

			this.isIssueLoading = true

			// 1: Validate policy
			// If invalid, just return. Errors are being shows in validatePolicy method
			const isPolicyValid = await this.validatePolicy()

			if (!isPolicyValid && !this.forceIssue) {
				this.isIssueLoading = false
				this.$modal.show("issuePolicyWarning")
				return
			}

			// 2: Issue
			const res = await api.issuePolicy({
				policyId: this.quoteId
			})
			this.isIssueLoading = false

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// this can happen if there is a problem from insurer integration
			if (res.data.issuePolicy.status === "failure") {
				this.$store.dispatch("showError", "the policy failed to issue")
				return
			}

			// Success
			// Redirect to issued policy
			this.$router.push({
				name: "PolicyDetail",
				params: {
					id: this.quoteId
				}
			})
		},
		async sendQuoteToInsurer() {
			const isValid = await this.$validator.validate()
			if (!isValid) return

			this.isSendQuoteLoading = true
			const context = this.context

			const emailContent = generateRequestQuoteFromInsurer({
				to: this.form.to,
				context,
				tenantId: this.$store.state.tenantId
			})

			if (emailContent.error) {
				// prettier-ignore
				const message = error => ({
					no_template: this.$t("email_has_no_template_for_tenant"),
					no_accepted_offer: this.$t("no_accepted_offer")
				}[error])

				this.$store.dispatch("showError", message(emailContent.error))
				this.isSendQuoteLoading = false
				return
			}

			const sendEmailRes = await api.sendNotification({
				input: {
					policyId: this.quoteId,
					emailMessage: emailContent
				}
			})

			this.isSendQuoteLoading = false
			if (sendEmailRes.errors) {
				this.$store.dispatch("showError", sendEmailRes.errors)
				return
			}

			this.$store.dispatch("showSuccess", this.$t("email_was_sent"))
			this.$modal.hide("send_quote")
		},
		async issuePolicyManually() {
			this.isIssueManuallyLoading = true
			const res = await api.issuePolicy({
				policyId: this.quoteId,
				issuerNumber: this.issuerNumber || null,
				isManual: true
			})
			this.isIssueManuallyLoading = false

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// Go to policy detail
			this.$router.push({
				name: "PolicyDetail",
				params: { id: this.quoteId }
			})
		},

		/**
		 * This method does an API call to check if policy is valid and can be issued or payment can be requested
		 * If policy is valid, it returns true
		 * Else it returns false
		 */
		async validatePolicy() {
			const validated = await api.validatePolicy({
				policyId: this.quoteId
			})
			// This query is rather special in terms of errors
			// errors are API errors whether errors_2 are errors to show to user
			if (
				validated.data.validatePolicy_2.errors ||
				validated.data.validatePolicy_2.errors_2
			) {
				if (validated.data.validatePolicy_2.errors)
					this.warnings = validated.data.validatePolicy_2.errors

				if (validated.data.validatePolicy_2.errors_2)
					this.warnings = validated.data.validatePolicy_2.errors_2

				return Promise.resolve(false)
			} else return Promise.resolve(true)
		},

		/**
		 * Method that will check if policy has accepted offer.
		 * If it doesn't have, it will show error and return false. Otherwise returns true.
		 * @returns true if it has otherwise false
		 */
		validateAcceptedOffer() {
			if (!this.acceptedOffer) {
				this.$store.dispatch("showError", this.$t("no_accepted_offer"))
				this.isIssueManuallyLoading = false
				return false
			}
			return true
		}
	}
}
</script>